import { Link } from "react-router-dom";
import { mediaItems } from "@/mocks/movies";
import { newsItems } from "@/mocks/news";
import type { MediaItem, NewsItem } from "@/types/media";
import MediaCard from "@/components/feature/MediaCard";
import SectionHeader from "@/components/feature/SectionHeader";
import HeroCarousel from "./components/HeroCarousel";
import CategoryShortcuts from "./components/CategoryShortcuts";
import NewsPreview from "./components/NewsPreview";

const allMedia = mediaItems as unknown as MediaItem[];
const allNews = newsItems as unknown as NewsItem[];

const STATS = [
  { value: "2000+", label: "收录影视作品" },
  { value: "40+", label: "覆盖国家地区" },
  { value: "12种", label: "细分影片类型" },
  { value: "每日", label: "资讯更新频率" },
];

export default function Home() {
  const featured = allMedia.filter((m) => !!m.hero).slice(0, 4);
  const latestMovies = allMedia
    .filter((m) => m.kind === "movie")
    .sort((a, b) => b.year - a.year || b.heat - a.heat)
    .slice(0, 10);
  const hotTv = allMedia
    .filter((m) => m.kind === "tv")
    .sort((a, b) => b.heat - a.heat)
    .slice(0, 8);
  const latestNews = [...allNews].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 5);

  return (
    <div>
      <HeroCarousel items={featured} />
      <CategoryShortcuts />

      <section className="w-full px-4 md:px-8 xl:px-16 py-12 md:py-16">
        <SectionHeader
          title="最新电影"
          subtitle="近期上线与热映的电影作品，按上映年份排序"
          moreTo="/category/movie"
        />
        {latestMovies.length === 0 ? (
          <div className="rounded-lg border border-background-200 bg-background-50 p-10 text-center text-sm text-foreground-500">
            暂无推荐内容
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
            {latestMovies.map((item) => (
              <MediaCard key={item.id} item={item} />
            ))}
          </div>
        )}
      </section>

      <section className="w-full bg-background-50 border-y border-background-200">
        <div className="w-full px-4 md:px-8 xl:px-16 py-12 md:py-16">
          <SectionHeader
            title="热播电视剧"
            subtitle="当前讨论度最高的剧集榜单，按热度排序"
            moreTo="/category/tv"
          />
          {hotTv.length === 0 ? (
            <div className="rounded-lg border border-background-200 bg-background-100 p-10 text-center text-sm text-foreground-500">
              暂无推荐内容
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
              {hotTv.map((item, i) => (
                <MediaCard key={item.id} item={item} rank={i + 1} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 xl:px-16 py-12 md:py-16">
        <SectionHeader
          title="影视资讯精选"
          subtitle="聚焦新片定档、行业动态与深度解读"
          moreTo="/news"
          moreLabel="进入资讯频道"
        />
        <NewsPreview items={latestNews} />
      </section>

      <section className="w-full px-4 md:px-8 xl:px-16 pb-16 md:pb-20">
        <div className="relative overflow-hidden rounded-2xl bg-primary-950 px-6 py-10 md:px-12 md:py-14">
          <div className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-primary-500/25"></div>
          <div className="absolute -left-20 bottom-0 w-56 h-56 rounded-full bg-accent-500/15"></div>
          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50/10 px-3 py-1 text-xs font-medium text-background-50">
                <i className="ri-planet-line"></i>
                关于天狼影院
              </span>
              <h2 className="mt-4 font-heading text-3xl md:text-4xl font-bold text-background-50 leading-tight">
                让每一次找片，都更快、更准、更有惊喜
              </h2>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-background-50/70">
                天狼影院是一个面向影迷的内容聚合平台。我们把电影、电视剧的资料、推荐与行业资讯整理在同一个地方，让你不必来回切换，就能完成从浏览到决策的全过程。
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-information-line text-lg"></i>
                  了解更多
                </Link>
                <Link
                  to="/category/movie"
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-50/10 text-background-50 text-sm font-medium hover:bg-background-50/20 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-compass-3-line text-lg"></i>
                  开始探索
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {STATS.map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-xl bg-background-50/10 border border-background-50/10 px-5 py-6"
                >
                  <p className="font-heading text-3xl font-bold text-accent-400">{stat.value}</p>
                  <p className="mt-1 text-xs text-background-50/70">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}